import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import elegantBallerina from '../assets/artistic_ballerina_shot.jpg';

const Contact = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setNewsletterError('');

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
      } else {
        setNewsletterError(data.error || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setNewsletterError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Handle contact form submission success/error messages
    const urlParams = new URLSearchParams(window.location.search);
    const contactSuccessMessage = document.getElementById('contact-success-message');
    const contactErrorMessage = document.getElementById('contact-error-message');

    if (urlParams.get('success') === 'true') {
      if (contactSuccessMessage) {
        contactSuccessMessage.style.display = 'block';
        // Scroll to success message
        contactSuccessMessage.scrollIntoView({ behavior: 'smooth' });
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (urlParams.get('error') === 'true') {
      if (contactErrorMessage) {
        contactErrorMessage.style.display = 'block';
        // Scroll to error message
        contactErrorMessage.scrollIntoView({ behavior: 'smooth' });
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  return (
    <Layout
      title="Contact & Newsletter | Prix du Canada Ballet Competition"
      description="Contact Prix du Canada ballet competition in Toronto. Send inquiries, join our newsletter for $50 off registration, and get early workshop access."
      keywords="contact ballet competition Canada, ballet competition Toronto contact, prix du canada newsletter, ballet competition discount, dance competition contact"
    >
      {/* Elegant Hero Section */}
      <section className="elegant-hero">
        <div className="hero-background">
          <img src={elegantBallerina} alt="Elegant ballet dancer" style={{opacity: 0.08}} />
        </div>
        <div className="container">
          <h1>Get in Touch</h1>
          <p style={{textAlign: 'center'}}>Questions about Prix du Canada? We're here to help dancers, parents, educators, and supporters on their journey to the stage.</p>
        </div>
      </section>

      {/* Contact Form Section - PRIMARY */}
      <section className="section" style={{background: 'var(--light-gray)'}}>
        <div className="container">
          <div style={{maxWidth: '600px', margin: '0 auto'}}>
            <div style={{textAlign: 'center', marginBottom: '3rem'}}>
              <div style={{width: '80px', height: '80px', background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', borderRadius: '50%', margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <span style={{color: 'white', fontSize: '2rem', fontWeight: 'bold'}}>✎</span>
              </div>
              <h2>Send Us a Message</h2>
              <p style={{fontSize: '1.1rem', color: 'var(--soft-gray)'}}>Have a specific question or want to learn more about taking the stage at Prix du Canada? We'd love to hear from you.</p>
            </div>
            
            <form action="https://formspree.io/f/manjkjaa" method="POST" style={{background: 'var(--warm-white)', padding: '2.5rem', borderRadius: '12px', boxShadow: '0 10px 30px var(--shadow-light)', borderTop: '6px solid var(--primary-gold)'}}>
              {/* Hidden field to set custom email recipient */}
              <input type="hidden" name="_to" value="info@prixducanada.com" />
              {/* Custom subject line */}
              <input type="hidden" name="_subject" value="New Contact Inquiry - Prix du Canada" />
              {/* Redirect after successful submission */}
              <input type="hidden" name="_next" value="https://prixducanada.com/contact.html?success=true" />
              
              <div style={{marginBottom: '1.5rem'}}>
                <label htmlFor="contact-name" style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--charcoal)', textAlign: 'left'}}>Name</label>
                <input type="text" id="contact-name" name="contact-name" required style={{width: '100%', padding: '1rem', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '1rem', transition: 'border-color 0.3s ease'}} />
              </div>
              
              <div style={{marginBottom: '1.5rem'}}>
                <label htmlFor="contact-email" style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--charcoal)', textAlign: 'left'}}>Email</label>
                <input type="email" id="contact-email" name="contact-email" required style={{width: '100%', padding: '1rem', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '1rem', transition: 'border-color 0.3s ease'}} />
              </div>
              
              <div style={{marginBottom: '1.5rem'}}>
                <label htmlFor="inquiry-type" style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--charcoal)', textAlign: 'left'}}>What can we help you with?</label>
                <select id="inquiry-type" name="inquiry-type" style={{width: '100%', padding: '1rem', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '1rem', transition: 'border-color 0.3s ease'}}>
                  <option value="registration">Registration & Competition Questions</option>
                  <option value="partnership">Partnership & Sponsorship</option>
                  <option value="volunteer">Volunteer Opportunities</option>
                  <option value="bug-report">Report a Bug</option>
                  <option value="general">General Inquiry</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div style={{marginBottom: '2rem'}}>
                <label htmlFor="message" style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--charcoal)', textAlign: 'left'}}>Message</label>
                <textarea id="message" name="message" rows="5" placeholder="Tell us how we can help you on your dance journey..." required style={{width: '100%', padding: '1rem', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '1rem', resize: 'vertical', transition: 'border-color 0.3s ease'}}></textarea>
              </div>
              
              <button type="submit" style={{width: '100%', padding: '1.2rem', background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', color: 'white', border: 'none', borderRadius: '6px', fontSize: '1.1rem', fontWeight: '600', cursor: 'pointer', transition: 'transform 0.3s ease', textTransform: 'uppercase', letterSpacing: '1px'}}>
                Send Message
              </button>
            </form>
            
            {/* Success/Error Messages */}
            <div id="contact-success-message" style={{display: 'none', background: '#d4edda', color: '#155724', padding: '1rem', borderRadius: '6px', marginTop: '1rem', textAlign: 'center'}}>
              <strong>Thank you!</strong> Your message has been sent successfully. We'll respond within 24-48 hours.
            </div>
            
            <div id="contact-error-message" style={{display: 'none', background: '#f8d7da', color: '#721c24', padding: '1rem', borderRadius: '6px', marginTop: '1rem', textAlign: 'center'}}>
              <strong>Oops!</strong> There was an error sending your message. Please try again or contact us directly at info@prixducanada.com.
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info & Newsletter Section - TWO COLUMN */}
      <section className="section" style={{background: 'var(--warm-white)'}}>
        <div className="container">
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'start'}}>

            {/* LEFT: Contact Information */}
            <div>
              <h2 style={{marginBottom: '2rem'}}>Contact Information</h2>

              <div className="card" style={{marginBottom: '1.5rem', borderLeft: '4px solid var(--primary-gold)', padding: '1.5rem'}}>
                <h3 style={{fontSize: '1.1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <span style={{color: 'var(--primary-gold)'}}>@</span> Email
                </h3>
                <p style={{fontSize: '1.1rem', marginBottom: '0.5rem'}}>
                  <a href="mailto:info@prixducanada.com" style={{color: 'var(--primary-gold)', textDecoration: 'none', fontWeight: '600'}}>info@prixducanada.com</a>
                </p>
                <p style={{color: 'var(--soft-gray)', fontSize: '0.95rem', margin: '0'}}>For inquiries, partnerships, and general questions</p>
              </div>

              <div className="card" style={{marginBottom: '1.5rem', borderLeft: '4px solid var(--deep-gold)', padding: '1.5rem'}}>
                <h3 style={{fontSize: '1.1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <span style={{color: 'var(--deep-gold)'}}>📍</span> Location
                </h3>
                <p style={{fontSize: '1.1rem', marginBottom: '0.5rem', fontWeight: '600'}}>Toronto, Ontario</p>
                <p style={{color: 'var(--soft-gray)', fontSize: '0.95rem', margin: '0'}}>Venue details announced with registration</p>
              </div>

              <div className="card" style={{borderLeft: '4px solid var(--primary-gold)', padding: '1.5rem', background: 'var(--soft-cream)'}}>
                <h3 style={{fontSize: '1.1rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <span style={{color: 'var(--primary-gold)'}}>⏱</span> Response Time
                </h3>
                <p style={{color: 'var(--charcoal)', fontSize: '0.95rem', margin: '0'}}>Expect a reply within 24-48 hours. During busy periods, please allow up to 3 business days.</p>
              </div>
            </div>

            {/* RIGHT: Newsletter Signup */}
            <div>
              <h2 style={{marginBottom: '2rem'}}>Join Our Newsletter</h2>

              <div className="card" style={{borderLeft: '4px solid var(--primary-gold)', padding: '1.5rem', minHeight: '100%'}}>
                <h3 style={{fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <span style={{color: 'var(--primary-gold)'}}>✉</span> Stay Connected
                </h3>
                <p style={{fontSize: '0.95rem', color: 'var(--soft-gray)', marginBottom: '1.5rem', lineHeight: '1.6'}}>
                  Subscribe to be updated first about upcoming workshops and masterclasses, collaboration opportunities, exclusive webinars and events, plus registration opening announcements.
                </p>

                <form onSubmit={handleNewsletterSubmit}>
                  <div style={{marginBottom: '1.5rem'}}>
                    <label htmlFor="newsletter-email" style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--charcoal)', textAlign: 'left'}}>Email Address</label>
                    <input
                      type="email"
                      id="newsletter-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isSuccess}
                      placeholder="your@email.com"
                      style={{
                        width: '100%',
                        padding: '0.9rem',
                        border: '2px solid #e0e0e0',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        opacity: isSuccess ? 0.6 : 1
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || isSuccess || !email}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: isSuccess
                        ? '#28a745'
                        : isSubmitting
                          ? 'var(--soft-gray)'
                          : 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: (isSubmitting || isSuccess) ? 'default' : 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      transition: 'background 0.3s ease'
                    }}
                  >
                    {isSuccess ? '✓ Subscribed' : isSubmitting ? 'Subscribing…' : 'Subscribe Now'}
                  </button>

                  <p style={{fontSize: '0.85rem', color: 'var(--soft-gray)', marginTop: '1rem', fontStyle: 'italic', textAlign: 'center'}}>
                    Unsubscribe anytime.
                  </p>

                  {newsletterError && (
                    <div style={{background: '#f8d7da', color: '#721c24', padding: '0.8rem', borderRadius: '6px', marginTop: '1rem', fontSize: '0.9rem'}}>
                      {newsletterError}
                    </div>
                  )}
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact; 